import { useEffect } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { authApi } from "@/shared/api/auth";
import { useAuth } from "@/shared/auth/store";
import { initAppUpdate } from "@/shared/native/appUpdate";
import { initNativePush } from "@/shared/native/push";
import { FONT_SCALE_VALUES, useEffectiveA11y } from "@/shared/hooks/useA11y";
import { useGlobalHotkeys } from "@/shared/hooks/useHotkeys";
import { AlarmCenter } from "./AlarmCenter";
import { AlertTtsAnnouncer } from "./AlertTtsAnnouncer";
import { BottomTabBar } from "./BottomTabBar";
import { EmergencyDispatchCenter } from "./EmergencyDispatchCenter";
import { HotkeyCheatsheet } from "./HotkeyCheatsheet";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { TrainingModeWatermark } from "./TrainingModeWatermark";

/**
 * Two-column shell.
 *
 * The shell is exactly one viewport tall and never participates in page
 * scroll. Only the `<main>` element owns a scroll context, so:
 *
 *   * the sidebar stays glued to the left,
 *   * the top bar stays glued to the top,
 *   * the account footer at the bottom of the sidebar is always visible.
 *
 * Font scale is applied here as a `transform: scale(...)` on a wrapper
 * *inside* <main>, not as a root `zoom`. That keeps the chrome (sidebar,
 * top bar) a fixed size while only the page content grows, and
 * compensating the wrapper's width by `100% / scale` means scale > 1
 * never triggers horizontal scroll.
 *
 * `transform-origin: 0 0` keeps the upper-left of the page glued to the
 * upper-left of the scroll viewport so scroll position stays predictable
 * across scale changes.
 */
export function AppShell() {
  // Lives here (not in AppProviders) because useNavigate() requires a
  // <Router> ancestor. AppShell only renders inside the routed tree.
  useGlobalHotkeys();

  // Refresh the cached "me" once on mount so a role / site-assignment change
  // (or a `must_change_password` flag set server-side) is reflected without a
  // re-login. A 401 is left to the axios interceptor, which clears the session
  // and bounces to /login.
  const setUser = useAuth((s) => s.setUser);
  useEffect(() => {
    let alive = true;
    authApi
      .me()
      .then((u) => {
        if (alive) setUser(u);
      })
      .catch(() => {
        /* 401 → interceptor handles; other errors → keep cached user */
      });
    return () => {
      alive = false;
    };
  }, [setUser]);

  // 네이티브(안드로이드/iOS) 앱에서만 FCM/APNs 푸시 토큰을 등록한다(웹=no-op).
  // 로그인 이후 셸 안에서 실행돼야 인증 헤더로 devices 를 저장할 수 있다.
  useEffect(() => {
    void initNativePush();
  }, []);

  // Play 인앱 업데이트 — 서버가 알린 하한 미만이면 강제, 그 외 새 버전은 백그라운드
  // 다운로드. 웹·iOS·사이드로드에서는 no-op 이라 기존 동작에 영향이 없다.
  useEffect(() => {
    void initAppUpdate();
  }, []);

  /**
   * Kiosk mode owns the whole panel. An unattended screen bolted to a wall
   * has no use for a search box, a sidebar or an account menu, and leaving
   * them mounted also puts the sticky top bar in a z-index fight with the
   * kiosk surface. Hide the chrome instead of layering around it.
   */
  const [params] = useSearchParams();
  const kioskMode = params.get("kiosk") === "1";

  const { fontScale } = useEffectiveA11y();
  const scale = FONT_SCALE_VALUES[fontScale];
  const scaled = scale !== 1;

  return (
    <div
      className="h-[100dvh] overflow-hidden flex bg-[var(--bg)] text-[var(--fg)]"
      /* 상단 안전영역 — index.html 이 `viewport-fit=cover` 라 웹뷰가 화면 전체를
         쓴다. 그대로 두면 상단바(시계·다이내믹 아일랜드) **아래로 콘텐츠가 들어가
         가려진다**(실기 아이폰에서 "통합 관제" 제목이 시계와 겹쳤다).
         패딩은 이 요소 안쪽이라 `bg-[var(--bg)]` 로 칠해져 상태바 영역이
         앱 배경색과 이어진다. 웹·데스크톱에서는 inset 이 0 이라 무영향. */
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Below lg the sidebar gives way to BottomTabBar — a 248px rail on a
          390px phone would leave nothing for the content. */}
      {/* `data-app-chrome` marks the shell furniture the wallboard hides when it
          maximises without native fullscreen — see global.css. */}
      {!kioskMode && (
        <div data-app-chrome="sidebar" className="hidden lg:flex h-full">
          <Sidebar />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col h-full">
        {!kioskMode && (
          <div data-app-chrome="topbar" className="contents">
            <TopBar />
          </div>
        )}
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          {/* `min-h-full` keeps short pages tall enough that children
              with `h-full` (DigitalTwinPage's canvas stage, modal-shaped
              views) actually receive a real height, while still letting
              long pages push the wrapper past the viewport and produce
              a normal scroll. */}
          <div
            className="min-h-full"
            style={
              scaled
                ? {
                    transform: `scale(${scale})`,
                    transformOrigin: "0 0",
                    width: `${100 / scale}%`,
                    minHeight: `${100 / scale}%`,
                    animation: "route-in var(--motion-base) var(--ease-out-soft) both",
                  }
                : {
                    animation:
                      "route-in var(--motion-base) var(--ease-out-soft) both",
                  }
            }
          >
            <Outlet />
            {/* 고정 탭바만큼 비워 마지막 줄이 탭바 아래 깔리지 않게 한다.
                높이는 탭바와 **같은 토큰**을 쓴다 — 따로 계산하면 어긋난다. */}
            {!kioskMode && (
              <div
                aria-hidden
                className="lg:hidden"
                style={{ height: "var(--tabbar-h)" }}
              />
            )}
          </div>
        </main>
      </div>
      {/* 탭바도 셸 가구다 — 벽면 최대화(egas-maximized)가 사이드바·상단바만
          숨기고 탭바를 남겨 두면, 네이티브 전체화면을 못 쓰는 웹뷰에서
          화면 하단이 계속 가려진다. */}
      {!kioskMode && (
        <div data-app-chrome="tabbar" className="contents">
          <BottomTabBar />
        </div>
      )}
      <HotkeyCheatsheet />
      <AlertTtsAnnouncer />
      <AlarmCenter />
      <EmergencyDispatchCenter />
      <TrainingModeWatermark />
    </div>
  );
}
